<script setup>
  import { ref, onMounted, watch, watchEffect} from 'vue';
  import { useUserStore } from "@/stores/UserStore";
  import { useGoogleAnalyticsStore } from '@/stores/GoogleAnalyticsStore';
  import BaseModal from './base/BaseModal.vue';
  import Toggle from './controls/Toggle.vue';
  import Icon from '@/components/icons/Icon.vue';
  import ModalSiteSettingsDetailViewSearchResults from './ModalSiteSettingsDetailViewSearchResults.vue';
  import ModalSiteSettingsDetailViewWindUnits from './ModalSiteSettingsDetailViewWindUnits.vue';
  import ModalSiteSettingsDetailViewDistanceUnits from './ModalSiteSettingsDetailViewDistanceUnits.vue';
  import ModalSiteSettingsDetailViewTemperatureUnits from './ModalSiteSettingsDetailViewTemperatureUnits.vue';
  import ModalSiteSettingsDetailViewPrecipitationUnits from './ModalSiteSettingsDetailViewPrecipitationUnits.vue';
  import ModalSiteSettingsDetailViewHeightUnits from './ModalSiteSettingsDetailViewHeightUnits.vue';
  import useConfigurationGlobals from '@/composables/useConfigurationGlobals';
  import useSettingsGlobals from '@/composables/useSettingsGlobals';
  import useModalNavigation from '@/composables/useModalNavigation';

  const { $IS_OPS_CONSOLE, $BRAND } = useConfigurationGlobals();
  const { $ADS_ALLOWED, $NC_WIND_TOGGLE_ALLOWED } = useSettingsGlobals();

  const SECURE_ORIGIN = `https://secure.${$BRAND.toLowerCase()}.com`;
  const SIGNOUT_URL = `${SECURE_ORIGIN}/?signout=true&app=wx&rd=Search.aspx`;
  const UPGRADE_URL = `${SECURE_ORIGIN}/features`;  
  const ACCOUNT_URL = `${SECURE_ORIGIN}/account`;  

  const emits = defineEmits(['update']);

  const props = defineProps({
    show: {
      type: Boolean,
      default: false
    },
  });

  const memberLevels = {
    '-1': 'guest',
    2: 'free',
    46: 'plus',
    49: 'pro',
    50: 'enterprise',
    56: 'gold'
  }

  const memberLevelColors = {
    '-1': 'var(--jwx-surface-level-1)',
    2: 'var(--jwx-surface-level-1)',
    46: 'hsl(193, 74%, 67%)',
    49: 'hsl(120, 46%, 61%)',
    56: 'hsl(47, 52%, 59%)'
  }

  const unitLabels = {
    wind: { mph: 'Miles Per Hour', kph: 'Kilometers Per Hour', kts: 'Knots', mps: 'Meters/Second' },
    distance: { mi: 'Miles', km: 'Kilometers' },
    temp: { f: 'Fahrenheit', c: 'Celsius' },
    precip: { in: 'Inches', mm: 'Millimeters', cm: 'Centimeters' },
    height: { ft: 'Feet', m: 'Meters' }
  };

  const searchResultsLabels = {
    windlist: 'Wind List',
    livewind: 'Live Wind',
    windmap: 'Wind Map'
  };

  const userStore = useUserStore();
  const googleAnalyticsStore = useGoogleAnalyticsStore();

  const showModal = ref(false);
  const {
    activeView,
    transitionName,
    modalBody,
    activeViewTitle,
    pushView,
    popView,
    onAfterEnter,
  } = useModalNavigation({
    wind: 'Wind Speed',
    distance: 'Distance',
    temp: 'Temperature',
    precip: 'Precipitation',
    height: 'Height',
    searchResults: 'Search Results',
  });

  function closeModal() {
    showModal.value = false;
    activeView.value = null;

    emits('close');
  } 

  function handleShowAdsChange(showAds) {
    const event = showAds ? 'Ads_Turned_On' : 'Ads_Turned_Off';

    googleAnalyticsStore.sendEvent(event);
  }
  
  onMounted(() => {
    watchEffect(() => showModal.value = props.show);

    // Fire only on the closed -> open edge of the authoritative source, so the
    // event isn't re-sent as showModal bounces during the open handshake.
    watch(
      () => props.show,
      (isOpen, wasOpen) => {
        if (!isOpen || wasOpen) return;

        googleAnalyticsStore.sendEvent('Show_Settings');
      }
    );
  })

  window.$EventBus.addEventListener('jwx-show-site-settings-modal', () => showModal.value = true);

</script>

<template>
  <BaseModal
    v-model="showModal"
    :allowOutsideClick="true"
    :height="$IS_OPS_CONSOLE ? 'short' : 'tall'"
    @close="closeModal"
  >
    <header class="jwx-modal__header">
      <h2 class="jwx-modal__title">
        <button v-if="activeView" type="button" class="jwx-modal__back-button" @click="popView">
          <Icon name="chevron-left" />
        </button>
        {{ activeView ? activeViewTitle : 'Settings' }}
      </h2>
    </header>

    <div ref="modalBody" class="jwx-modal__body">
      <Transition :name="transitionName" mode="out-in" @after-enter="onAfterEnter">

        <!-- Main view -->
        <div v-if="!activeView" key="main">
          <div v-if="!$IS_OPS_CONSOLE" class="jwx-modal__section">
            <div class="jwx-modal__section-body">
              <ul class="jwx-block jwx-table-view" data-surface="1">
                <li class="jwx-table-view__row">
                  <div class="jwx-table-view__row-content jwx-flex jwx-justify-between">
                    <p class="jwx-modal__text jwx-font-weight-700">
                      {{ userStore.userName }}
                    </p>
                    <span class="jwx-badge jwx-font-weight-700" :style="`--jwx-badge-padding: .25rem .75rem; --jwx-badge-background-color: ${memberLevelColors[userStore.memberLevel]}`">{{ memberLevels[userStore.memberLevel][0].toUpperCase() + memberLevels[userStore.memberLevel].slice(1) }}</span>
                  </div>
                </li>
                <li v-if="userStore.memberLevel < 50" class="jwx-table-view__row">
                  <a
                    class="jwx-table-view__action-row"
                    data-type="link"
                    :href="UPGRADE_URL"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <p class="jwx-table-view__row-label jwx-font-weight-400">Upgrade Membership</p>
                    <div class="jwx-table-view__action-row-symbol">
                      <Icon name="chevron-right" />
                    </div>
                  </a>
                </li>                
                <li v-if="userStore.memberLevel >= 2" class="jwx-table-view__row">
                  <a
                    class="jwx-table-view__action-row"
                    data-type="link"
                    :href="ACCOUNT_URL"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <p class="jwx-table-view__row-label jwx-font-weight-400">Manage Account</p>
                    <div class="jwx-table-view__action-row-symbol">
                      <Icon name="chevron-right" />
                    </div>
                  </a>
                </li>                
                <li v-if="userStore.memberLevel >= 2" class="jwx-table-view__row">
                  <a
                    class="jwx-table-view__action-row"
                    data-type="link"
                    :href="SIGNOUT_URL"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <p class="jwx-table-view__row-label jwx-font-weight-400">Sign Out</p>
                    <div class="jwx-table-view__action-row-symbol">
                      <Icon name="chevron-right" />
                    </div>
                  </a>
                </li>                
              </ul>
            </div>
          </div>

          <div v-if="!$IS_OPS_CONSOLE" class="jwx-modal__section">
            <div class="jwx-modal__section-header">
              <div class="jwx-modal__section-title">Support</div>
            </div>
            <div class="jwx-modal__section-body">
              <ul class="jwx-block jwx-table-view" data-surface="1">
                <li class="jwx-table-view__row">
                  <a
                    class="jwx-table-view__action-row"
                    data-type="link"
                    href="https://help.tempest.earth/hc/en-us/categories/200419268-Membership-Services"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <p class="jwx-table-view__row-label jwx-font-weight-400">Help Center</p>
                    <div class="jwx-table-view__action-row-symbol">
                      <Icon name="chevron-right" />
                    </div>
                  </a>
                </li>
                <li class="jwx-table-view__row">
                  <a
                    class="jwx-table-view__action-row"
                    data-type="link"
                    href="https://help.tempest.earth/hc/en-us/requests/new"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <p class="jwx-table-view__row-label jwx-font-weight-400">Contact Us/Feedback</p>
                    <div class="jwx-table-view__action-row-symbol">
                      <Icon name="chevron-right" />
                    </div>
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div class="jwx-modal__section">
            <div class="jwx-modal__section-header">
              <h3 class="jwx-modal__section-title">Units</h3>
            </div>
            <div class="jwx-modal__section-body">
              <ul class="jwx-block jwx-table-view" data-surface="1">
                <li class="jwx-table-view__row">
                  <button type="button" class="jwx-table-view__action-row" @click="pushView('wind')">
                    <p class="jwx-table-view__row-label jwx-font-weight-400">Wind Speed</p>
                    <div class="jwx-table-view__action-row-detail">
                      <span class="jwx-table-view__row-value">{{ unitLabels.wind[userStore.unitsWind] }}</span>
                      <Icon name="chevron-right" />
                    </div>
                  </button>
                </li>
                <li class="jwx-table-view__row">
                  <button type="button" class="jwx-table-view__action-row" @click="pushView('distance')">
                    <p class="jwx-table-view__row-label jwx-font-weight-400">Distance</p>
                    <div class="jwx-table-view__action-row-detail">
                      <span class="jwx-table-view__row-value">{{ unitLabels.distance[userStore.unitsDistance] }}</span>
                      <Icon name="chevron-right" />
                    </div>
                  </button>
                </li>
                <li class="jwx-table-view__row">
                  <button type="button" class="jwx-table-view__action-row" @click="pushView('temp')">
                    <p class="jwx-table-view__row-label jwx-font-weight-400">Temperature</p>
                    <div class="jwx-table-view__action-row-detail">
                      <span class="jwx-table-view__row-value">{{ unitLabels.temp[userStore.unitsTemp] }}</span>
                      <Icon name="chevron-right" />
                    </div>
                  </button>
                </li>
                <li class="jwx-table-view__row">
                  <button type="button" class="jwx-table-view__action-row" @click="pushView('precip')">
                    <p class="jwx-table-view__row-label jwx-font-weight-400">Precipitation</p>
                    <div class="jwx-table-view__action-row-detail">
                      <span class="jwx-table-view__row-value">{{ unitLabels.precip[userStore.unitsPrecip] }}</span>
                      <Icon name="chevron-right" />
                    </div>
                  </button>
                </li>
                <li class="jwx-table-view__row">
                  <button type="button" class="jwx-table-view__action-row" @click="pushView('height')">
                    <p class="jwx-table-view__row-label jwx-font-weight-400">Height</p>
                    <div class="jwx-table-view__action-row-detail">
                      <span class="jwx-table-view__row-value">{{ unitLabels.height[userStore.unitsHeight] }}</span>
                      <Icon name="chevron-right" />
                    </div>
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <div v-if="!$IS_OPS_CONSOLE" class="jwx-modal__section">
            <div class="jwx-modal__section-header">
              <div class="jwx-modal__section-title">Misc</div>
            </div>
            <div class="jwx-modal__section-body">
              <ul class="jwx-block jwx-table-view" data-surface="1">
                <li class="jwx-table-view__row">
                  <button type="button" class="jwx-table-view__action-row" @click="pushView('searchResults')">
                    <p class="jwx-table-view__row-label jwx-font-weight-400">Search Results</p>
                    <div class="jwx-table-view__action-row-detail">
                      <span class="jwx-table-view__row-value">{{ searchResultsLabels[userStore.searchResultsDisplay] }}</span>
                      <Icon name="chevron-right" />
                    </div>
                  </button>
                </li>
              </ul>
              <p class="jwx-modal__section-description">Select the screen you would like to view the search results on when initiating a search from a screen other than the Wind List, Map, or Live Wind.</p>
              <ul v-if="userStore.memberLevel >= 46" class="jwx-block jwx-table-view" data-surface="1">
                <li v-if="$NC_WIND_TOGGLE_ALLOWED && userStore.memberLevel >= 49" class="jwx-table-view__row">
                  <div class="jwx-table-view__row-content">
                    <label for="show-nc-wind-toggle" class="jwx-table-view__row-label jwx-font-weight-400">Show Nearcast™ Wind Toggle</label>
                    <div class="jwx-table-view__row-actions">
                      <Toggle
                        v-model="userStore.showNCWindToggle"
                        name="show-nc-wind-toggle"
                        id="show-nc-wind-toggle"
                      />
                    </div>
                  </div>
                </li>
                <li v-if="$ADS_ALLOWED" class="jwx-table-view__row">
                  <div class="jwx-table-view__row-content">
                    <label for="show-ads-toggle" class="jwx-table-view__row-label jwx-font-weight-400">Show Ads</label>
                    <div class="jwx-table-view__row-actions">
                      <Toggle
                        v-model.boolean="userStore.showAds"
                        @input:change="handleShowAdsChange"
                        name="show-ads-toggle"
                        id="show-ads-toggle"
                      />
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <ModalSiteSettingsDetailViewWindUnits v-else-if="activeView === 'wind'" />
        <ModalSiteSettingsDetailViewDistanceUnits v-else-if="activeView === 'distance'" />
        <ModalSiteSettingsDetailViewTemperatureUnits v-else-if="activeView === 'temp'" />
        <ModalSiteSettingsDetailViewPrecipitationUnits v-else-if="activeView === 'precip'" />
        <ModalSiteSettingsDetailViewHeightUnits v-else-if="activeView === 'height'" />
        <ModalSiteSettingsDetailViewSearchResults v-else-if="activeView === 'searchResults'" />

      </Transition>
    </div>

    <div class="jwx-modal__footer">
      <div class="jwx-modal__actions">
        <button
          type="button"
          @click.prevent="closeModal"
          class="jwx-button"
        >
          Close
      </button>
      </div>
    </div>
  </BaseModal>
</template>